/**
 * The expanded row under a shop in the dashboard's shops table (owner ruling
 * 2026-08-12): that shop's last 7 daily reports in the selected period, with
 * a link through to the full ledger. Presentational -- useShopDailyDetails
 * owns the fetch so collapsing does not discard it.
 *
 * Payout ratio is v1's net-of-tax convention (dashboardHelpers.payoutRatioPct)
 * from each row's own stored figures; over 100% (a loss day) it turns error
 * red, the one piece of colour here -- net profit stays plain even when
 * negative. Net profit is sales - payout - STORED tax, never a recomputed rate.
 */
import { Link as RouterLink } from 'react-router-dom';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import { MoneyText } from '../../components/MoneyText.tsx';
import { MONEY_NUMERIC_SX, palette } from '../../theme.ts';
import { payoutRatioPct, payoutRatioText } from './dashboardHelpers.ts';
import type { ShopDailyDetail as DetailState } from './useShopDailyDetails.ts';

/** Deep-links the reports ledger already filtered to this shop. */
export function shopReportsPath(shopId: number): string {
  return `/reports?shop=${shopId}`;
}

function RatioCell({
  payoutMinor,
  salesMinor,
  taxMinor,
}: Readonly<{ payoutMinor: number; salesMinor: number; taxMinor: number }>): React.JSX.Element {
  const pct = payoutRatioPct(payoutMinor, salesMinor, taxMinor);
  const overspent = pct !== null && pct > 100;
  return (
    <TableCell align="right" sx={{ ...MONEY_NUMERIC_SX, color: overspent ? palette.destructive : undefined }}>
      {payoutRatioText(payoutMinor, salesMinor, taxMinor)}
    </TableCell>
  );
}

export interface ShopDailyDetailProps {
  shopId: number;
  detail: DetailState | undefined;
}

export function ShopDailyDetail({ shopId, detail }: Readonly<ShopDailyDetailProps>): React.JSX.Element {
  if (detail === undefined || detail.kind === 'loading') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
        <CircularProgress size={16} />
        <Typography variant="body2">Loading daily reports...</Typography>
      </Box>
    );
  }

  if (detail.kind === 'error') {
    // Quiet note, not an alert: one shop's detail failing is not a page error.
    return (
      <Typography variant="body2" sx={{ color: palette.textSecondary, py: 1.5 }}>
        {detail.message}
      </Typography>
    );
  }

  return (
    <Box sx={{ py: 1 }}>
      {detail.rows.length === 0 ? (
        <Typography variant="body2" sx={{ color: palette.textSecondary, py: 0.5 }}>
          No daily reports for this shop in this period.
        </Typography>
      ) : (
        <Table size="small" aria-label="Daily reports">
          <TableHead>
            {/* No 1NL revenue column by design: device fees are per MONTH, so a
                per-day revenue figure would be incomplete or amortized fiction. */}
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell align="right">Gross sales</TableCell>
              <TableCell align="right">Payout</TableCell>
              <TableCell align="right">Payout ratio</TableCell>
              <TableCell align="right">Net profit</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {detail.rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.submission_date}</TableCell>
                <TableCell align="right">
                  <MoneyText minor={row.sales_minor} currencyCode={row.currency_code} size="0.8rem" weight={600} />
                </TableCell>
                <TableCell align="right">
                  <MoneyText minor={row.payout_minor} currencyCode={row.currency_code} size="0.8rem" weight={600} />
                </TableCell>
                <RatioCell payoutMinor={row.payout_minor} salesMinor={row.sales_minor} taxMinor={row.tax_minor} />
                <TableCell align="right">
                  {/* Plain even when negative -- a loss day is a fact, not an alarm. */}
                  <MoneyText
                    minor={row.sales_minor - row.payout_minor - row.tax_minor}
                    currencyCode={row.currency_code}
                    size="0.8rem"
                    weight={600}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Link component={RouterLink} to={shopReportsPath(shopId)} variant="body2" sx={{ display: 'inline-block', mt: 1 }}>
        View all reports →
      </Link>
    </Box>
  );
}

export default ShopDailyDetail;
